import React, { useState, useEffect, useReducer } from "react";

const EligibilityContext = React.createContext();

const defaults = {
  modalOpen: false,
  step: 1,
  diseaseFund: null,
  role: "patient",
  medications: [""],
  USResidence: null,
  insuranceCoverage: null,
  insuranceTypes: [],
  stateOfResidence: "",
  residents: 1,
  income: "",
  notificationsOptIn: false,
  travelAssistance: false,
  errorEncountered: false,
  response: null,
  supportPrograms: []
};

const EligibilityProvider = ({ children }) => {
  // global state
  const [modalOpen, setModalOpen] = useState(defaults.modalOpen);
  const [step, setStep] = useState(defaults.step);

  // search state
  const [diseaseFund, setDiseaseFund] = useState(defaults.diseaseFund);
  const [role, setRole] = useState(defaults.role);

  // eligibility state
  const [medications, setMedications] = useState(defaults.medications);
  const [supportPrograms, setSupportPrograms] = useState(defaults.supportPrograms);
  const [USResidence, setUSResidence] = useState(defaults.USResidence);
  const [insuranceCoverage, setInsuranceCoverage] = useState(
    defaults.insuranceCoverage
  );
  const [insuranceTypes, dispatchInsuranceTypes] = useReducer(
    (insuranceTypes, { type, value }) => {
      switch (type) {
        case "add":
          return [...insuranceTypes, value];
        case "remove":
          return insuranceTypes.filter(
            (insurance) => insurance.Id !== value.Id
          );
        case "clear":
          return [];
        default:
          return insuranceTypes;
      }
    },
    defaults.insuranceTypes
  );

  const [stateOfResidence, setStateOfResidence] = useState(
    defaults.stateOfResidence
  );
  const [zip, setZip] = useState(defaults.zip);
  const [residents, setResidents] = useState(defaults.residents);
  const [income, setIncome] = useState(defaults.income);
  const [travelAssistance, setTravelAssistance] = useState(
    defaults.travelAssistance
  );

  // resources state
  const [notificationsOptIn, setNotificationsOptIn] = useState(false);

  // error state
  const [errorEncountered, setErrorEncountered] = useState(
    defaults.errorEncountered
  );

  // response data
  const [response, setResponse] = useState(defaults.response);

  // If the user changes the disease fund, clear all selected medications
  useEffect(() => {
    setMedications(defaults.medications);
  }, [diseaseFund]);

  const closeModal = () => {
    setModalOpen(defaults.modalOpen);
  };

  const renderError = () => {
    setModalOpen(true);
    setStep(10);
    resetEligibility();
    setErrorEncountered(true);
    setDiseaseFund(defaults.diseaseFund);
  };

  const reset = () => {
    setStep(defaults.step);
    resetEligibility();
  };

  const resetEligibility = () => {
    setMedications(defaults.medications);
    setUSResidence(defaults.USResidence);
    setInsuranceCoverage(defaults.insuranceCoverage);
    setStateOfResidence(defaults.stateOfResidence);
    dispatchInsuranceTypes({ type: "clear" });
    setResidents(defaults.residents);
    setIncome(defaults.income);
    setNotificationsOptIn(defaults.notificationsOptIn);
    setResponse(defaults.response);
  };

  const state = {
    modalOpen,
    step,
    diseaseFund,
    role,
    medications,
    supportPrograms,
    USResidence,
    insuranceCoverage,
    insuranceTypes,
    stateOfResidence,
    zip,
    residents,
    income,
    travelAssistance,
    response,
    notificationsOptIn,
    errorEncountered,
  };

  const actions = {
    setModalOpen,
    setStep,
    setDiseaseFund,
    setRole,
    setMedications,
    setSupportPrograms,
    setUSResidence,
    setInsuranceCoverage,
    dispatchInsuranceTypes,
    setStateOfResidence,
    setZip,
    setResidents,
    setIncome,
    setTravelAssistance,
    setResponse,
    setNotificationsOptIn,
    setErrorEncountered,
    closeModal,
    renderError,
    reset,
  };

  return (
    <EligibilityContext.Provider value={{ state, actions }}>
      {children}
    </EligibilityContext.Provider>
  );
};

export { EligibilityContext, EligibilityProvider };
